import type {
  JsonObject,
  ObservationDataset,
  RuntimeCommand,
} from '../contracts/index.ts';
import type {
  RuntimeSession,
  RuntimeSessionListener,
  RuntimeSessionSnapshot,
} from './RuntimeSession.ts';
import { z } from 'zod';
import {
  observationDatasetSchema,
  runtimeCommandSchema,
  runtimeSessionSnapshotSchema,
} from '../contracts/index.ts';

export interface RuntimeExecutionAdapter {
  dispatch: (command: RuntimeCommand) => Promise<void>;
  snapshot: () => Promise<RuntimeSessionSnapshot>;
  subscribe: (listener: RuntimeSessionListener) => () => void;
  dispose: () => void;
}

export class MainThreadExecutionAdapter implements RuntimeExecutionAdapter {
  private disposed = false;

  constructor(private readonly session: RuntimeSession) {}

  async dispatch(command: RuntimeCommand): Promise<void> {
    this.assertUsable();
    await this.session.dispatch(runtimeCommandSchema.parse(command));
  }

  async snapshot(): Promise<RuntimeSessionSnapshot> {
    this.assertUsable();
    return this.session.getSnapshot();
  }

  subscribe(listener: RuntimeSessionListener): () => void {
    this.assertUsable();
    return this.session.subscribe(listener);
  }

  dispose(): void {
    if (this.disposed)
      return;
    this.disposed = true;
    this.session.dispose();
  }

  private assertUsable(): void {
    if (this.disposed)
      throw new Error('主线程运行适配器已经释放');
  }
}

export type RuntimeWorkerRequest
  = {
    requestId: string;
    sessionId: string;
    type: 'initialize';
    capabilityKey: string;
    config: JsonObject;
    seed: number;
  }
  | {
    requestId: string;
    sessionId: string;
    type: 'dispatch';
    command: RuntimeCommand;
  }
  | {
    requestId: string;
    sessionId: string;
    type: 'snapshot';
  }
  | {
    requestId: string;
    sessionId: string;
    type: 'dispose';
  };

export type RuntimeWorkerMessage
  = {
    type: 'response';
    requestId: string;
    sessionId: string;
    ok: boolean;
    snapshot?: RuntimeSessionSnapshot;
    datasets?: ObservationDataset[];
    error?: string;
  }
  | {
    type: 'snapshot';
    sessionId: string;
    snapshot: RuntimeSessionSnapshot;
    datasets?: ObservationDataset[];
  };

export interface RuntimeWorkerTransport {
  postMessage: (message: RuntimeWorkerRequest) => void;
  addEventListener: (
    type: 'message' | 'error',
    listener: EventListener,
  ) => void;
  removeEventListener: (
    type: 'message' | 'error',
    listener: EventListener,
  ) => void;
  terminate: () => void;
}

interface PendingWorkerRequest {
  resolve: (snapshot?: RuntimeSessionSnapshot) => void;
  reject: (error: Error) => void;
  timeout: ReturnType<typeof globalThis.setTimeout>;
}

export class WorkerRuntimeExecutionAdapter
implements RuntimeExecutionAdapter {
  private readonly listeners = new Set<RuntimeSessionListener>();
  private readonly pending = new Map<string, PendingWorkerRequest>();
  private disposed = false;
  private sequence = 0;
  private lastSnapshot: RuntimeSessionSnapshot | null = null;

  constructor(
    private readonly worker: RuntimeWorkerTransport,
    private readonly sessionId: string,
    private readonly requestTimeoutMs = 30000,
    private readonly onDatasets?: (datasets: ObservationDataset[]) => void,
  ) {
    worker.addEventListener('message', this.handleMessage);
    worker.addEventListener('error', this.handleError);
  }

  async dispatch(command: RuntimeCommand): Promise<void> {
    await this.request({
      type: 'dispatch',
      command: runtimeCommandSchema.parse(command),
    });
  }

  async initialize(input: {
    capabilityKey: string;
    config: JsonObject;
    seed: number;
  }): Promise<RuntimeSessionSnapshot> {
    const snapshot = await this.request({
      type: 'initialize',
      capabilityKey: input.capabilityKey,
      config: input.config,
      seed: input.seed,
    });
    if (!snapshot)
      throw new Error('Worker 初始化后未返回运行快照');
    return snapshot;
  }

  async snapshot(): Promise<RuntimeSessionSnapshot> {
    const snapshot = await this.request({ type: 'snapshot' });
    if (!snapshot)
      throw new Error('Worker 未返回运行快照');
    return snapshot;
  }

  subscribe(listener: RuntimeSessionListener): () => void {
    this.assertUsable();
    this.listeners.add(listener);
    if (this.lastSnapshot)
      listener(structuredClone(this.lastSnapshot));
    return () => this.listeners.delete(listener);
  }

  dispose(): void {
    if (this.disposed)
      return;
    this.disposed = true;
    const requestId = this.nextRequestId();
    try {
      this.worker.postMessage({
        type: 'dispose',
        requestId,
        sessionId: this.sessionId,
      });
    }
    catch {
      // The worker may already be unavailable; local cleanup still proceeds.
    }
    this.worker.removeEventListener('message', this.handleMessage);
    this.worker.removeEventListener('error', this.handleError);
    this.worker.terminate();
    for (const pending of this.pending.values()) {
      globalThis.clearTimeout(pending.timeout);
      pending.reject(new Error('Worker 运行适配器已释放'));
    }
    this.pending.clear();
    this.listeners.clear();
  }

  private request(
    payload:
      | Pick<
        Extract<RuntimeWorkerRequest, { type: 'initialize' }>,
        'type' | 'capabilityKey' | 'config' | 'seed'
      >
      | Pick<Extract<RuntimeWorkerRequest, { type: 'dispatch' }>, 'type' | 'command'>
      | Pick<Extract<RuntimeWorkerRequest, { type: 'snapshot' }>, 'type'>,
  ): Promise<RuntimeSessionSnapshot | undefined> {
    this.assertUsable();
    const requestId = this.nextRequestId();
    return new Promise((resolve, reject) => {
      const timeout = globalThis.setTimeout(() => {
        this.pending.delete(requestId);
        reject(new Error(`Worker 请求超时：${payload.type}`));
      }, this.requestTimeoutMs);
      this.pending.set(requestId, { resolve, reject, timeout });
      try {
        this.worker.postMessage({
          ...payload,
          requestId,
          sessionId: this.sessionId,
        } as RuntimeWorkerRequest);
      }
      catch (error) {
        globalThis.clearTimeout(timeout);
        this.pending.delete(requestId);
        reject(error instanceof Error ? error : new Error('Worker 消息发送失败'));
      }
    });
  }

  private readonly handleMessage: EventListener = (rawEvent) => {
    const event = rawEvent as MessageEvent<RuntimeWorkerMessage>;
    const message = event.data;
    if (!message || message.sessionId !== this.sessionId)
      return;
    if (message.type === 'snapshot') {
      try {
        this.publishDatasets(message.datasets);
        this.publishSnapshot(message.snapshot);
      }
      catch {
        // Invalid unsolicited snapshots are ignored at the worker boundary.
      }
      return;
    }
    const pending = this.pending.get(message.requestId);
    if (!pending)
      return;
    this.pending.delete(message.requestId);
    globalThis.clearTimeout(pending.timeout);
    if (!message.ok) {
      pending.reject(new Error(message.error || 'Worker 运行失败'));
      return;
    }
    try {
      this.publishDatasets(message.datasets);
      const snapshot = message.snapshot
        ? this.publishSnapshot(message.snapshot)
        : undefined;
      pending.resolve(snapshot ? structuredClone(snapshot) : undefined);
    }
    catch (error) {
      pending.reject(
        error instanceof Error
          ? error
          : new Error('Worker 返回了无效运行快照'),
      );
    }
  };

  private readonly handleError: EventListener = (rawEvent) => {
    const event = rawEvent as ErrorEvent;
    const error = new Error(event.message || 'Worker 发生未知错误');
    for (const pending of this.pending.values()) {
      globalThis.clearTimeout(pending.timeout);
      pending.reject(error);
    }
    this.pending.clear();
    if (this.lastSnapshot) {
      this.publishSnapshot({
        ...this.lastSnapshot,
        status: 'error',
        error: {
          code: 'WorkerError',
          message: error.message,
          operation: 'worker',
          recoverable: false,
          occurredAt: new Date().toISOString(),
        },
      });
    }
  };

  private publishSnapshot(
    snapshot: RuntimeSessionSnapshot,
  ): RuntimeSessionSnapshot {
    const parsed = runtimeSessionSnapshotSchema.parse(snapshot);
    this.lastSnapshot = structuredClone(parsed);
    for (const listener of this.listeners) {
      try {
        listener(structuredClone(parsed));
      }
      catch {
        // A view subscriber must not break the worker message channel.
      }
    }
    return parsed;
  }

  private publishDatasets(
    rawDatasets: ObservationDataset[] | undefined,
  ): void {
    if (!rawDatasets || !this.onDatasets)
      return;
    const datasets = z.array(observationDatasetSchema).max(1000).parse(
      rawDatasets,
    );
    this.onDatasets(structuredClone(datasets));
  }

  private assertUsable(): void {
    if (this.disposed)
      throw new Error('Worker 运行适配器已经释放');
  }

  private nextRequestId(): string {
    this.sequence += 1;
    return `${this.sessionId}-${this.sequence}`;
  }
}
